import ClearIcon from "@mui/icons-material/Clear";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import * as React from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/shared/foundation/primitives";
import { validateFormula } from "@/shared/math/expression-evaluator";
import { actions, useVizDispatch } from "@/shared/state/visualization";

import { default as sectionStyles } from "../../SettingsSection.module.css";

import styles from "./PlotSeriesHeader.module.css";

export interface TransformFieldProps {
  panelId: string;
  seriesId: string;
  /** The currently applied expression. Empty string means no transform. */
  appliedExpression: string;
  /** Human-readable series name used for aria-labels. */
  seriesLabel: string;
}

interface TransformFormValues {
  expression: string;
}

/**
 * Per-series Y-axis transform formula editor.
 */
export function TransformField({
  panelId,
  seriesId,
  appliedExpression,
  seriesLabel,
}: TransformFieldProps) {
  const vizStateDispatch = useVizDispatch();

  const { register, handleSubmit, reset, watch, formState } =
    useForm<TransformFormValues>({
      defaultValues: { expression: appliedExpression },
      mode: "onChange",
    });

  // Keep the form in sync when the applied expression changes externally
  React.useEffect(() => {
    reset({ expression: appliedExpression });
  }, [appliedExpression, reset]);

  const expressionValue = watch("expression");
  const canApply = formState.isValid && expressionValue !== appliedExpression;

  const onSubmit = ({ expression }: TransformFormValues) => {
    const newTransform = expression
      ? { type: "formula" as const, expression }
      : undefined;
    vizStateDispatch(
      actions.setSeriesConfig(panelId, seriesId, { transform: newTransform }),
    );
  };

  const handleClear = () => {
    reset({ expression: "" });
    vizStateDispatch(
      actions.setSeriesConfig(panelId, seriesId, { transform: undefined }),
    );
  };

  const { ref: expressionRef, ...expressionField } = register("expression", {
    validate: (value) => {
      if (!value) {
        return true;
      }
      const result = validateFormula(value);
      return result.valid || result.error;
    },
  });

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
      <TextField
        {...expressionField}
        inputRef={expressionRef}
        className={sectionStyles.input}
        label="f(y) = "
        error={!!formState.errors.expression}
        helperText={formState.errors.expression?.message}
        placeholder="3 * y + 7 or sqrt(y)"
        inputProps={{
          className: sectionStyles.inputLabel,
          "aria-label": `Y transform formula for ${seriesLabel}`,
          maxLength: 200,
        }}
        InputProps={{
          className: sectionStyles.inputLabel,
          endAdornment: expressionValue ? (
            <InputAdornment position="end">
              <IconButton
                aria-label={`Clear Y transform formula for ${seriesLabel}`}
                onClick={handleClear}
                edge="end"
                size="small"
              >
                <ClearIcon style={{ fontSize: "1rem" }} />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        InputLabelProps={{
          className: sectionStyles.inputLabel,
          shrink: true,
        }}
        margin="dense"
        size="small"
        fullWidth
      />
      {canApply && (
        <div className={styles.transformApplyRow}>
          <Button type="submit" variant="outlined" size="small">
            Apply
          </Button>
        </div>
      )}
    </form>
  );
}
